import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../firebase';
import { collection, query, orderBy, where, getDocs, limit, startAt } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faSearch, faTimes, faExclamationTriangle, faFilter } from '@fortawesome/free-solid-svg-icons';
import { format, startOfDay, endOfDay, isValid, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Form state for filter inputs
  const [filterInputs, setFilterInputs] = useState({
    email: '',
    startDate: '',
    endDate: '',
    status: ''
  });

  // Active filters state (only updated when Apply is clicked)
  const [activeFilters, setActiveFilters] = useState({
    email: '',
    startDate: '',
    endDate: '',
    status: ''
  });

  const fetchOrders = useCallback(
    async (activeFilters, page = 1) => {
      setLoading(true);
      setError(null);
      try {
        let constraints = [orderBy('accepted_at', 'desc')];

        if (activeFilters.email) {
          constraints.push(
            where(
              'client_email',
              '==',
              activeFilters.email.toLowerCase()
            )
          );
        }

        if (activeFilters.startDate && activeFilters.endDate) {
          const startDate = startOfDay(parseISO(activeFilters.startDate));
          const endDate = endOfDay(parseISO(activeFilters.endDate));
          if (isValid(startDate) && isValid(endDate)) {
            constraints.push(
              where('accepted_at', '>=', startDate),
              where('accepted_at', '<=', endDate)
            );
          }
        }

        // Calculate the startAt document for the current page
        const startAtIndex = (page - 1) * ordersPerPage;
        if (startAtIndex > 0) {
          // Fetch the document at the startAt index
          const snapshot = await getDocs(query(collection(db, 'orders'), ...constraints, limit(startAtIndex)));
          if (snapshot.docs.length === startAtIndex) {
            constraints.push(startAt(snapshot.docs[snapshot.docs.length - 1]));
          } else {
            // Handle the case where there are fewer documents than startAtIndex
            setOrders([]);
            setTotalPages(1);
            setTotalOrders(0);
            setLoading(false);
            return;
          }
        }

        constraints.push(limit(ordersPerPage));

        const ordersRef = collection(db, 'orders');
        const q = query(ordersRef, ...constraints);

        const ordersSnapshot = await getDocs(q);
        const newOrders = ordersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setOrders(newOrders);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to fetch orders. Please try again later.');
        setLoading(false);
      }
    },
    [ordersPerPage]
  );

  const fetchTotalOrdersCount = useCallback(async (activeFilters) => {
    try {
      let constraints = [];

      if (activeFilters.email) {
        constraints.push(
          where(
            'client_email',
            '==',
            activeFilters.email.toLowerCase()
          )
        );
      }

      if (activeFilters.startDate && activeFilters.endDate) {
        const startDate = startOfDay(parseISO(activeFilters.startDate));
        const endDate = endOfDay(parseISO(activeFilters.endDate));
        if (isValid(startDate) && isValid(endDate)) {
          constraints.push(
            where('accepted_at', '>=', startDate),
            where('accepted_at', '<=', endDate)
          );
        }
      }

      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, ...constraints);

      const snapshot = await getDocs(q);
      setTotalOrders(snapshot.size);
      setTotalPages(Math.ceil(snapshot.size / ordersPerPage));
    } catch (error) {
      console.error("Error fetching total orders count:", error);
    }
  }, [ordersPerPage]);

  useEffect(() => {
    fetchTotalOrdersCount(activeFilters);
  }, [activeFilters, fetchTotalOrdersCount]);

  useEffect(() => {
    fetchOrders(activeFilters, currentPage);
  }, [activeFilters, currentPage, fetchOrders]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterInputs(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setActiveFilters(filterInputs);
    setCurrentPage(1); // Reset to first page when filters are applied
  };

  const clearFilters = () => {
    const emptyFilters = {
      email: '',
      startDate: '',
      endDate: '',
      status: ''
    };
    setFilterInputs(emptyFilters);
    setActiveFilters(emptyFilters);
    setCurrentPage(1); // Reset to first page when filters are cleared
  };

  const handleViewOrder = (order) => {
    navigate(`/admin/orders/${order.id}`, { state: { order } });
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'accepted': 'bg-green-100 text-green-800',
      'completed': 'bg-blue-100 text-blue-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const getPaginationButtons = () => {
    const buttons = [];
    for (let i = 1; i <= totalPages; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`px-4 py-2 rounded-lg mx-1 ${currentPage === i
            ? 'bg-primary text-white'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const hasActiveFilters = Object.values(activeFilters).some(value => value !== '');
  const hasFilterChanges = JSON.stringify(filterInputs) !== JSON.stringify(activeFilters);

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-6">Orders</h1>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Email Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search by Email
            </label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={filterInputs.email}
                onChange={handleInputChange}
                placeholder="customer@example.com"
                className="w-full cursor-pointer outline-1 outline rounded-lg border-gray-300 pl-10 pr-4 py-1 focus:ring-primary focus:border-primary"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
            </div>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              name="startDate"
              value={filterInputs.startDate}
              onChange={handleInputChange}
              className="w-full outline-1 cursor-pointer outline rounded-lg pr-4 py-1 border-gray-300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              name="endDate"
              value={filterInputs.endDate}
              onChange={handleInputChange}
              className="w-full outline-1 cursor-pointer outline rounded-lg pr-4 py-1 border-gray-300"
            />
          </div>

          {/* Status Filter */}
          {/* <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={filterInputs.status}
              onChange={handleInputChange}
              className="w-full outline-1 cursor-pointer outline rounded-lg px-2 py-1 border-gray-300"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div> */}
        </div>

        {/* Filter Actions */}
        <div className="mt-4 flex justify-end space-x-4">
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              Clear Filters
            </button>
          )}
          <button
            onClick={applyFilters}
            disabled={!hasFilterChanges}
            className={`flex items-center px-4 py-2 rounded-lg ${hasFilterChanges
              ? 'bg-primary text-white hover:bg-primary-dark'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
          >
            <FontAwesomeIcon icon={faFilter} className="mr-2" />
            Apply Filters
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {orders.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No orders found matching your filters.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {console.table(orders)}
              {orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {order.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{order.client_first_name} {order.client_last_name}</div>
                    <div className="text-xs text-gray-400">{order.client_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {(() => {
                      const d = new Date(order.accepted_at * 1000);
                      const s = ["th", "st", "nd", "rd"],
                        n = d.getDate(),
                        o = s[(n % 10 > 3 || ~~(n % 100 / 10) == 1) ? 0 : n % 10];
                      return `${n}${o} ${d.toLocaleString('en-GB', { month: 'short', year: 'numeric' })} ${d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    ${order.total_price.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 capitalize inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <button
                      onClick={() => handleViewOrder(order)}
                      className="text-primary hover:text-primary-dark transition-colors"
                    >
                      <FontAwesomeIcon icon={faEye} className="mr-2" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex justify-center mt-4">
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-2 rounded-lg mx-1 bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Previous
        </button>
        {getPaginationButtons()}
        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-2 rounded-lg mx-1 bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Orders;